import uuid from 'uuid/v4';

import { attachXidToMultipleEvents } from '../AttachXID';
import {
  BatchFlushCallback,
  RetryQueueOptions,
} from '../resilienceQueue';
import getMetricsCollector, {
  MetricsCollector,
} from '../resilienceQueue/Metrics';
import { Context } from '../types';
import { XIDPromise } from '../XIDPromise';


import { EVENT_MISMATCH } from './constants';
import { DEFAULT_REQUEST_TIMEOUT } from './defaults';
import getIntermediateBatchableQueue, {
  IntermediateBatchableQueue
} from './intermediateResilienceQueue';
import {
  sendEvents
} from './sendEvents';
import {
  BaseSegmentEvent,
  LibraryMetadataDef,
  PackagedEvent,
  SegmentBatchDef,
  SegmentEvent,
  SegmentEventTypes,
  SegmentIdentifyEventDef,
  SegmentIdentifyEventTraitsDef,
  SegmentMetadataDef,
  SegmentProperties,
  SegmentScreenEventDef,
  SegmentTrackEventDef,
  SegmentTrackPropertiesDef,
} from './types';
import User from './user';
import {
  buildContext,
  prepareEventProperties,
} from './util';

export type Options = {
  apiHost: string,
  retryQueueOptions?: RetryQueueOptions,
  retryQueuePrefix: string,
  product: string,
  requestTimeout?: number,
  xidPromiseGetter: () => ReturnType<typeof XIDPromise>,
  enableNewEventSender: boolean,
};

const BUNDLED_INTEGRATIONS = ['BeforeSend', 'Segment.io'];
const UNBUNDLED_INTEGRATIONS = ['Amplitude'];

export const METADATA: SegmentMetadataDef = {
  bundled: BUNDLED_INTEGRATIONS,
  unbundled: UNBUNDLED_INTEGRATIONS,
};

export default class EventProcessor {

  private user: User;
  private options: Required<Options>;

  private resilienceQueue: IntermediateBatchableQueue;

  private gasv3BatchUrl: string;
  private metrics: MetricsCollector;
  private xidPromiseCallback: ReturnType<typeof XIDPromise>;

  constructor(options: Options) {
    this.user = new User();
    this.options = {
      ...options,
      requestTimeout: options.requestTimeout || DEFAULT_REQUEST_TIMEOUT,
      retryQueueOptions: options.retryQueueOptions || {},
    };

    this.xidPromiseCallback = options.xidPromiseGetter();
    this.gasv3BatchUrl = `https://${options.apiHost}/batch`;
    this.metrics = getMetricsCollector();

    this.resilienceQueue = getIntermediateBatchableQueue(
      options.retryQueuePrefix,
      options.product,
      this.options.retryQueueOptions
    );
    this.resilienceQueue.setEventProcessor(this);

    // Flagged until @segment/analytics.js-core is removed
    if (this.options.enableNewEventSender) {
      this.resilienceQueue.start(this.sendEvents);
    }
  }

  getUser(): User {
    return this.user;
  }

  track(eventName: string, builtEvent: SegmentTrackPropertiesDef, context: Context, callback?: () => void) {
    const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.TRACK);
    const eventWithoutMessageId: Omit<SegmentTrackEventDef, 'messageId'> = {
      ...baseEvent,
      type: SegmentEventTypes.TRACK,
      properties: prepareEventProperties(builtEvent),
      event: eventName,
    };
    const event: SegmentTrackEventDef = {
      ...eventWithoutMessageId,
      messageId: this.createMessageId(eventWithoutMessageId),
    };
    const packagedEvent = this.packageEvent(event);

    // For event mismatches, we need to send the event through so we can recieive them in splunk.
    this.resilienceQueue.addItem(packagedEvent, eventName !== EVENT_MISMATCH);
    if (callback) {
      callback();
    }
  }

  page(eventName: string, builtEvent: SegmentProperties, context: Context, callback?: () => void) {
    const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.PAGE);
    const eventWithoutMessageId: Omit<SegmentScreenEventDef, 'messageId'> = {
      ...baseEvent,
      type: SegmentEventTypes.PAGE,
      properties: {
        ...prepareEventProperties(builtEvent),
        name: eventName,
      },
      name: eventName,
      category: null,
    };
    const event: SegmentScreenEventDef = {
      ...eventWithoutMessageId,
      messageId: this.createMessageId(eventWithoutMessageId),
    };
    const packagedEvent = this.packageEvent(event);

    this.resilienceQueue.addItem(packagedEvent, true);
    if (callback) {
      callback();
    }
  }

  // Segment uses the identifier to update user id which we have already done in the analyticsWebClient.ts
  identify(_identifier: string, builtEvent: SegmentIdentifyEventTraitsDef, context: Context, callback?: () => void) {
    const baseEvent = this.buildBaseEvent(context, SegmentEventTypes.IDENTIFY);
    const eventWithoutMessageId: Omit<SegmentIdentifyEventDef, 'messageId'> = {
      ...baseEvent,
      type: SegmentEventTypes.IDENTIFY,
      traits: prepareEventProperties(builtEvent),
    };
    const event: SegmentIdentifyEventDef = {
      ...eventWithoutMessageId,
      messageId: this.createMessageId(eventWithoutMessageId),
    };
    const packagedEvent = this.packageEvent(event);
    this.resilienceQueue.addItem(packagedEvent, true);
    if (callback) {
      callback();
    }
  }

  private buildBaseEvent(
    context: Context,
    type: SegmentEventTypes,
  ): Omit<BaseSegmentEvent, 'messageId'> {
    const clonedContext = prepareEventProperties(context);
    const segmentContext = buildContext(clonedContext);
    return {
      context: segmentContext,
      timestamp: new Date().toISOString(),
      type,
      integrations: {},
      userId: this.user.getUserId(),
      anonymousId: this.user.getAnonymousId(),
      _metadata: METADATA,
    };
  }

  private createMessageId(
    event: object
  ): string {
    return `ajs-${uuid()}`;
  }

  private packageEvent(
    event: SegmentEvent
  ): PackagedEvent {
    const { apiHost } = this.options;
    return {
      headers: {
        'Content-Type': 'text/plain',
      },
      msg: event,
      url: `https://${apiHost}/${event.type.charAt(0)}`,
    };
  }

  // Using anonymous function so it can have the BatchFlushCallback type associated with it
  // And to allow it to not need bind when passing through to ResilieceQueue.
  public sendEvents: BatchFlushCallback<PackagedEvent> = async (items, callback) => {
    const httpRetryCount = this.resilienceQueue.getGlobalRetryCount();
    const metricsPayload = this.metrics.getMetricsPayload();
    const metadata: LibraryMetadataDef = {
      ...metricsPayload,
      httpRetryCount,
    };
    for (let key in metadata) {
      // @ts-ignore Some keys maybe a string, but these will never equal 0
      if (metadata[key] === 0) {
        // @ts-ignore Save space in requests by removing metrics with no impact
        delete metadata[key];
      }
    }

    const eventsWithXID = await this.attachXIDs(items);

    // Calculating sentAt after the XID generation as this may take some time.
    const sentAt = new Date().toISOString();
    const unpackagedEvents = eventsWithXID.map(item => {
      item.msg.sentAt = sentAt;
      return item.msg;
    });

    const batchBody: SegmentBatchDef = {
      batch: unpackagedEvents,
      sentAt,
      metadata,
    };

    try {
      const response = await sendEvents({
        url: this.gasv3BatchUrl,
        batch: batchBody,
        timeout: this.options.requestTimeout,
      });

      this.metrics.subtractFromMetrics(metricsPayload);
      callback(null, response);
    } catch (error) {
      callback(error, null);
    }
  }

  private async attachXIDs(items: PackagedEvent[]): Promise<PackagedEvent[]> {
    if (this.xidPromiseCallback) {
      return attachXidToMultipleEvents(items, this.xidPromiseCallback);
    }
    return items;
  }
}
